// Road-name simplification (Morgan, Aug 6: "when roads are long on
// directions — especially roads with slashes — only pick one name, the
// first one, simplify"). The real-world specimen, Umbria, Aug 6:
//   "Turn left onto Strada Castel dell'Aquila/SP38/Strada provinciale 38
//    Sambucetole - Castel dell'Aquila. Continue on SP38/Strada
//    provinciale 38 Sambucetole - Castel dell'Aquila."
// Three names for one road, twice. A driver needs one, once:
//   "Turn left onto Strada Castel dell'Aquila. Continue on SP38."
//
// The regex removes each "/alternate-name" chunk. The character class
// deliberately EXCLUDES sentence punctuation (. , ; :) so a removal
// stops at the end of the clause — the first draft included "." and ate
// "…dell'Aquila. Continue on SP38" in one bite, deleting the entire
// second instruction. Includes the typographic apostrophe (’) alongside
// the straight one for names like dell’Aquila as some sources emit it.
// Decimal distances ("0.3 miles") contain no slash, so numbers pass
// through untouched.
export function simplifyRoadName(instruction: string): string {
  return instruction.replace(/\s*\/[A-Za-z0-9 '’()À-ſ-]+/g, "");
}

// ---------------------------------------------------------------------------
// ROAD-NAME SANITATION (card-copy audit, Aug 24)
//
// The "Mostly via …" / "Takes …" / "Mostly on …" slots are supposed to hold
// ROAD NAMES. Some sources give a road name; others give only a turn-by-turn
// instruction, and the road is dug out of it. When that dig came up short the
// instruction went to the card whole. Every specimen below is real, taken off
// a rendered card during the audit:
//
//   "Mostly via exit 217A on the left to merge onto US-36 W/Denver Boulder
//    Turnpike and I-25 N via the ramp to Ft Collins"      (Denver → Boulder)
//   "Mostly via I-95 N Toll road Entering Connecticut"    (NYC → New Haven)
//   "Mostly via US-9 and US-9 N/N Highland Ave Continue to follow US-9 N"
//                                                         (Ossining → Rhinebeck)
//   "Mostly via Au rond-point, prendre la 2e sortie et continuer sur D637"
//                                                         (Paris → Fontainebleau)
//   "Mostly on Hutchinson-River-Parkway"                  (NYC → New Haven)
//   "Mostly via I-95 N, US-1 N Toll road, and I-95 N Toll road"  (Boston → Portsmouth)
//   "Takes Cabrillo Highway and Cabrillo Highway South"   (SF → Santa Cruz)
//
// Three separate faults, fixed in three separate passes below. None of them
// changes which roads are chosen — only how the chosen name is rendered.
// ---------------------------------------------------------------------------

/** Connectors after which an instruction finally names the road. Greedy `.*`
 *  keeps the LAST one, so "exit 5B to stay on I 380" yields "I 380" rather
 *  than "5B to stay on I 380". `\bonto\b` cannot match inside "Toronto" —
 *  there is no word boundary mid-word. */
const ROAD_AFTER_CONNECTOR =
  /.*\b(?:merge onto|stay on|to follow|continue on(?:to)?|onto|continuer sur|prendre)\s+/i;

/** Strip maneuver instructions, POI annotations and direction clauses from a
 *  road-name slot, and expand a hyphen slug. Returns "" when nothing survives. */
export function sanitizeRoadName(raw: string): string {
  let s = String(raw ?? "").replace(/\s+/g, " ").trim();
  if (!s) return "";

  // 1. The road usually follows a connector — keep the tail.
  if (ROAD_AFTER_CONNECTOR.test(s)) s = s.replace(ROAD_AFTER_CONNECTOR, "");

  // 2. Trailing navigation chatter that is not part of any road's name.
  s = s
    .replace(/\s*\(.*?\)/g, "")
    .replace(/\s+continue\s+(?:to\s+follow|on(?:to)?)\b.*$/i, "")
    .replace(/\s+entering\b.*$/i, "")
    .replace(/\s+via\s+the\s+ramp\b.*$/i, "")
    .replace(/\s+(?:toward|towards|for|until)\b.*$/i, "")
    .replace(/\s+(?:pass|passing)\s+by\b.*$/i, "")
    .replace(/\s+(?:on|to)\s+the\s+(?:left|right)\b.*$/i, "")
    .replace(/\s+ramp\b.*$/i, "");

  // 3. Leading maneuver verbs and exit numbers.
  s = s
    .replace(/^(?:au\s+rond-point|à\s+la\s+sortie)\s*,?\s*/i, "")
    .replace(/^(?:take|takes|use|follow|head|keep|bear|turn|slight)\b\s*/i, "")
    .replace(/^exit\s+\S+\s*/i, "")
    .replace(/^the\s+/i, "");

  // 4. One road, one name — drop "/alternate-name" chunks (rule above).
  s = simplifyRoadName(s);

  // 5. Hyphen SLUG → spaces. Deliberately requires TWO OR MORE hyphens joining
  //    alphabetic words, which is what a slug looks like
  //    ("Hutchinson-River-Parkway"). A single hyphen is left alone: it may be
  //    the road's real name, and it is how every route number is written
  //    ("I-95", "US-1", "CA-1", "AZ-69"). So "Wickenburg-Prescott Highway"
  //    survives untouched — I could not establish it is a slug rather than the
  //    genuine name, and mangling a real name is the worse error.
  if (/^[A-Za-z][\w’']*(?:-[A-Za-z][\w’']*){2,}$/.test(s)) s = s.replace(/-/g, " ");

  // 6. ROUTE-NUMBER + SUFFIX SLUG (found on the fix-pass re-run, Asheville:
  //    "Fastest — via I-26-East"). Rule 5 cannot catch this: it requires every
  //    hyphen to be followed by a LETTER, and "I-26-East" has a digit after
  //    the first. Only a carriageway/designation suffix is joined back with a
  //    space, so "I-26-East" → "I-26 East" while the route number "I-26"
  //    itself, and genuine hyphenated names like "Aguila-Congress Junction
  //    Highway", are untouched.
  s = s.replace(
    /(\d)-(North|South|East|West|Business|Bus|Truck|Alternate|Alt|Spur|Loop|Bypass|Connector|[NSEW])\b/gi,
    "$1 $2",
  );

  return s.replace(/[\s,]+$/, "").replace(/\s+/g, " ").trim();
}

/** Comparison key for "is this the same road under another dressing?".
 *  Ignores toll/direction suffixes and punctuation, so "I-95 N",
 *  "I-95 N Toll road" and "I 95" all collapse to one road, and
 *  "Cabrillo Highway" absorbs "Cabrillo Highway South". */
function roadDedupeKey(name: string): string {
  let k = name.toLowerCase().replace(/\btoll road\b/g, " ").trim();
  // TRAILING direction only. Stripping cardinals anywhere would fold "West
  // Buckeye Road" into "Buckeye Road" — two different roads on the
  // Phoenix → Prescott scenic option. A cardinal at the END is a carriageway
  // suffix ("US-9 N", "Cabrillo Highway South"); at the START it is the name.
  let prev = "";
  while (prev !== k) {
    prev = k;
    k = k.replace(/[\s,]+(?:north|south|east|west|[nsew])\s*$/i, "").trim();
  }
  return k.replace(/[^a-z0-9]/g, "");
}

/** Sanitize, drop empties, and collapse the same road listed twice.
 *  First occurrence wins — callers pass these longest-driven-first. */
export function cleanRoadNameList(names: string[], max?: number): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names ?? []) {
    const name = sanitizeRoadName(raw);
    if (!name) continue;
    const key = roadDedupeKey(name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(name);
    if (max != null && out.length >= max) break;
  }
  return out;
}
